import type { Results } from "@agency/shared";
import confetti from "canvas-confetti";
import { store } from "../store/GameStore.js";
import { clear, el, formatSeconds } from "./dom.js";

export class ResultsModal {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly meta: HTMLElement;
  private readonly countdown: HTMLElement;
  private readonly body: HTMLElement;
  private timer?: number;

  constructor(parent: HTMLElement) {
    this.root = el("div", "modal hidden");
    const panel = el("div", "panel");
    this.title = el("h2", undefined, "Project shipped!");
    this.meta = el("div", "server-line", "");
    this.countdown = el("div", "countdown", "");
    this.body = el("div");
    panel.append(this.title, this.meta, this.countdown, this.body);
    this.root.appendChild(panel);
    this.root.addEventListener("click", (event) => {
      if (event.target === this.root) this.hide();
    });
    parent.appendChild(this.root);
  }

  mount(): void {
    store.on("results", (results) => this.show(results));
    store.on("projectStarted", () => this.hide());
    // Transient messages can be missed across a reconnect: reconcile against
    // the restored phase and the welcome payload.
    store.on("phase", (phase) => {
      if (phase === "active") this.hide();
    });
    store.on("welcome", (welcome) => {
      const hidden = this.root.classList.contains("hidden");
      if (hidden && store.phase === "celebrating" && welcome.lastResults) {
        this.show(welcome.lastResults);
      }
    });
  }

  show(results: Results): void {
    this.title.textContent = `🎉 ${results.projectName} shipped!`;
    this.meta.textContent = `Completed in ${formatSeconds(results.durationMs / 1000)} of team time.`;

    clear(this.body);
    const table = el("table", "results");
    const head = el("tr");
    for (const h of ["#", "Dev", "Worked", "Share", ""]) head.appendChild(el("th", undefined, h));
    const thead = el("thead");
    thead.appendChild(head);

    const tbody = el("tbody");
    for (const entry of results.entries) {
      const tr = el("tr");
      tr.appendChild(el("td", undefined, String(entry.rank)));
      tr.appendChild(el("td", undefined, entry.nickname));
      tr.appendChild(el("td", undefined, formatSeconds(entry.workSeconds)));
      tr.appendChild(el("td", undefined, `${entry.sharePct.toFixed(1)}%`));
      const tags = el("td");
      if (entry.mvp) tags.appendChild(el("span", "badge", "MVP"));
      if (!entry.present) tags.appendChild(el("span", "badge early", "left early"));
      tr.appendChild(tags);
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);
    this.body.appendChild(table);

    this.root.classList.remove("hidden");
    this.fireConfetti();
    this.tickCountdown(results.nextStartsAt);
    this.timer = window.setInterval(() => this.tickCountdown(results.nextStartsAt), 500);
  }

  hide(): void {
    this.root.classList.add("hidden");
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  private tickCountdown(nextStartsAt: number): void {
    const remaining = Math.max(0, nextStartsAt - (Date.now() + store.serverOffset));
    this.countdown.textContent = `Next project in ${Math.ceil(remaining / 1000)}s…`;
    if (remaining <= 0 && this.timer) {
      window.clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  private fireConfetti(): void {
    const opts = { particleCount: 90, spread: 70, origin: { y: 0.4 } };
    confetti(opts);
    setTimeout(() => confetti({ ...opts, angle: 120, origin: { x: 1, y: 0.5 } }), 200);
    setTimeout(() => confetti({ ...opts, angle: 60, origin: { x: 0, y: 0.5 } }), 320);
  }
}
